/**
 * Report Service
 * API client for user, community, and ritual report endpoints
 */

import { apiClient } from "@/lib/api-client"

// User report reasons
export type ReportReason =
  | "HARASSMENT"
  | "SPAM"
  | "INAPPROPRIATE_CONTENT"
  | "HATE_SPEECH"
  | "IMPERSONATION"
  | "OTHER"

// Community report reasons
export type ReportCommunityReason =
  | "HARMFUL_ENVIRONMENT"
  | "INAPPROPRIATE_CONTENT"
  | "SPAM"
  | "HATE_SPEECH"
  | "MISLEADING"
  | "OTHER"

// Ritual report reasons
export type ReportRitualReason =
  | "HARMFUL_PRACTICES"
  | "MISLEADING"
  | "INAPPROPRIATE_CONTENT"
  | "SPAM"
  | "CULTURAL_INSENSITIVITY"
  | "OTHER"

interface CreateUserReportRequest {
  reportedUserId: string
  reason: ReportReason
  details?: string
}

interface CreateCommunityReportRequest {
  communityId: string
  reason: ReportCommunityReason
  details?: string
}

interface CreateRitualReportRequest {
  ritualId: string
  reason: ReportRitualReason
  details?: string
}

interface CreateReportResponse {
  success: boolean
  data: { id: string }
  message: string
}

// User report
export async function createReport(data: CreateUserReportRequest): Promise<CreateReportResponse> {
  const response = await apiClient.post<CreateReportResponse>("/api/reports", data)
  return response.data
}

// Community report
export async function createCommunityReport(
  data: CreateCommunityReportRequest,
): Promise<CreateReportResponse> {
  const response = await apiClient.post<CreateReportResponse>("/api/reports/communities", data)
  return response.data
}

interface CreateCommentReportRequest {
  commentId: string
  reason: ReportReason
  details?: string
}

// Comment report
export async function createCommentReport(
  data: CreateCommentReportRequest,
): Promise<CreateReportResponse> {
  const response = await apiClient.post<CreateReportResponse>("/api/reports/comments", data)
  return response.data
}

// Ritual report
export async function createRitualReport(
  data: CreateRitualReportRequest,
): Promise<CreateReportResponse> {
  const response = await apiClient.post<CreateReportResponse>("/api/reports/rituals", data)
  return response.data
}
